import React from 'react';
import { Box, Button, Flex, Text, Title } from '@mantine/core';
import { IconTargetArrow } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  name: string;
  locale: string;
  /** Сколько дел ждёт человека — от этого зависит сама фраза. */
  todoCount: number;
  isMobile?: boolean;
  onSetGoal?: () => void;
}

const greetingKey = (hour: number): string => {
  if (hour < 6) return 'dashboard.greetNight';
  if (hour < 12) return 'dashboard.greetMorning';
  if (hour < 18) return 'dashboard.greetDay';
  return 'dashboard.greetEvening';
};

/**
 * Шапка главной. Крупная строка — не число в плитке, а то, что человеку нужно
 * знать: сколько дел ждёт или что сегодня свободно. Приветствие и дата уходят
 * в подпись: они приятны, но решения не меняют.
 *
 * Появление — единственная анимация на экране (t-stagger из transitions.dev):
 * одна сцена при загрузке вместо десяти мелких эффектов.
 */
export const Hero: React.FC<HeroProps> = ({ name, locale, todoCount, isMobile, onSetGoal }) => {
  const { t } = useTranslation();
  const [isShown, setIsShown] = React.useState(false);
  const now = new Date();

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setIsShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const date = now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Flex justify="space-between" align="flex-end" gap="lg" wrap="wrap">
      <Box className={`t-stagger ${isShown ? 'is-shown' : ''}`}>
        <Title
          order={1}
          size={isMobile ? 'h3' : 'h2'}
          className="t-stagger-line t-stagger-line--1"
          style={{ letterSpacing: '-0.01em', lineHeight: 1.15 }}
        >
          {todoCount > 0 ? t('dashboard.heroTodo', { count: todoCount }) : t('dashboard.heroClear')}
        </Title>
        <Text c="dimmed" size="sm" mt={6} className="t-stagger-line t-stagger-line--2">
          {t(greetingKey(now.getHours()), { name })}, {date}
        </Text>
      </Box>
      {/* Без раздела «Цели» кнопки нет. */}
      {onSetGoal && (
        <Button
          variant="light"
          leftSection={<IconTargetArrow size={16} />}
          onClick={onSetGoal}
          visibleFrom="xs"
        >
          {t('dashboard.setGoal')}
        </Button>
      )}
    </Flex>
  );
};
